// Platform-agnostic personal-notes list for the Notes surface.
//
// Notes are DeviceGroup-only personal state (§2.10 `0x90 NOTE_UPSERT` /
// `0x91 NOTE_DELETE`) mirrored across the user's own devices. They are
// stored as flat `NoteRecord` rows keyed on the stable hex `note_id`;
// upserts are last-writer-wins, deletes remove the row. This hook reads
// the host-supplied `NotesPersister` (Dexie liveQuery on web, the
// AsyncStorage slice on desktop) and returns a live array sorted by
// `updatedAt` descending (most-recently-edited first).
//
// Host-neutral: it reads/writes nothing itself. When no persister is
// supplied (a host that hasn't wired notes yet) it returns a stable
// empty array.

import { useEffect, useMemo, useState } from 'react';

import type { NoteRecord, NotesPersister } from './persistenceAdapters';

const EMPTY: readonly NoteRecord[] = Object.freeze([]);

/**
 * Live array of the user's personal notes, most-recently-edited first.
 *
 * @param persister  Backing store; when omitted the hook returns a
 *                   frozen empty array (no subscription).
 */
export function useNotes(persister?: NotesPersister): readonly NoteRecord[] {
  const [rows, setRows] = useState<readonly NoteRecord[]>(EMPTY);

  useEffect(() => {
    if (!persister) {
      setRows(EMPTY);
      return;
    }

    let cancelled = false;

    if (persister.subscribe) {
      const unsubscribe = persister.subscribe((next) => {
        if (!cancelled) setRows(next);
      });
      return () => {
        cancelled = true;
        unsubscribe();
      };
    }

    void persister
      .list()
      .then((next) => {
        if (!cancelled) setRows(next);
      })
      .catch(() => {
        if (!cancelled) setRows(EMPTY);
      });

    return () => {
      cancelled = true;
    };
  }, [persister]);

  return useMemo(() => {
    if (rows.length === 0) return EMPTY;
    return [...rows].sort((a, b) => b.updatedAt - a.updatedAt);
  }, [rows]);
}
